import {
  categoryForMetric,
  EventLogMetricCategory,
  labelForMetric,
} from '@chiroup/core/constants/metricCategories';
import {
  CalendarDays,
  Clipboard,
  Database,
  DollarSign,
  Maximize2,
  Settings,
  Users,
} from 'lucide-react';
import React from 'react';
import Sparkline from './charts/Sparkline';
import { computeTrend, formatTrend, trendArrow, trendColor } from './utils/trend';

type Props = {
  metricKey: string;
  value: number;
  previousValue: number;
  series?: number[];
  onClick?: () => void;
  onExpand?: () => void;
};

const INVERSE_METRICS = new Set([
  'npi_rejections',
  'enrollment_rejections',
  'format_837_rejections',
  'provider_loop_errors',
  'referring_provider_errors',
  'no_show_cancel_fees_added',
  'users_removed',
]);

const CATEGORY_COLORS: Record<
  EventLogMetricCategory,
  { border: string; bg: string; icon: string }
> = {
  schedule: { border: 'border-l-green-500', bg: 'hover:bg-green-50/30', icon: 'text-green-600' },
  clinical: { border: 'border-l-blue-500', bg: 'hover:bg-blue-50/30', icon: 'text-blue-600' },
  billing: { border: 'border-l-amber-500', bg: 'hover:bg-amber-50/30', icon: 'text-amber-600' },
  patients: { border: 'border-l-rose-500', bg: 'hover:bg-rose-50/30', icon: 'text-rose-600' },
  databases: {
    border: 'border-l-indigo-500',
    bg: 'hover:bg-indigo-50/30',
    icon: 'text-indigo-600',
  },
  clinic_settings: {
    border: 'border-l-cyan-500',
    bg: 'hover:bg-cyan-50/30',
    icon: 'text-cyan-600',
  },
  user_settings: {
    border: 'border-l-purple-500',
    bg: 'hover:bg-purple-50/30',
    icon: 'text-purple-600',
  },
  resources: {
    border: 'border-l-orange-500',
    bg: 'hover:bg-orange-50/30',
    icon: 'text-orange-600',
  },
};

const CATEGORY_ICONS: Record<
  EventLogMetricCategory,
  React.ComponentType<{ className?: string }>
> = {
  schedule: CalendarDays,
  clinical: Clipboard,
  billing: DollarSign,
  patients: Users,
  databases: Database,
  clinic_settings: Settings,
  user_settings: Users,
  resources: Users,
};

const MetricCard: React.FC<Props> = ({
  metricKey,
  value,
  previousValue,
  series,
  onClick,
  onExpand,
}) => {
  const trend = computeTrend(value, previousValue);
  const isInverse = INVERSE_METRICS.has(metricKey);
  const category = categoryForMetric(metricKey);
  const colors = CATEGORY_COLORS[category];
  const IconComponent = CATEGORY_ICONS[category];

  return (
    <div
      className={`group relative flex flex-col items-stretch text-left rounded-lg border border-gray-200 bg-white p-4 ${colors.bg} hover:scale-[1.03] hover:shadow-md transition-all duration-300`}
    >
      {onExpand && (
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            onExpand();
          }}
          className="absolute top-2 right-2 p-1 rounded text-gray-300 hover:text-gray-600 hover:bg-gray-100 transition"
          title="Expand chart"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
      )}
      <button
        type="button"
        onClick={onClick}
        className="flex flex-col items-stretch text-left focus:outline-none"
      >
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <IconComponent className={`h-4 w-4 ${colors.icon} flex-shrink-0`} />
            <span className="text-xl font-bold text-gray-900 tabular-nums truncate">
              {value.toLocaleString()}
            </span>
          </div>
          <span
            className={`text-xs font-semibold flex-shrink-0 ${trendColor(trend.direction, isInverse)}`}
          >
            {trendArrow(trend.direction)} {formatTrend(trend)}
          </span>
        </div>
        <div className="text-xs text-gray-600 line-clamp-2 min-h-[2rem]">
          {labelForMetric(metricKey)}
        </div>
        <div className="mt-2 -mx-1 overflow-hidden">
          {series && series.length > 0 ? (
            <Sparkline values={series} height={24} />
          ) : (
            <div className="h-6" />
          )}
        </div>
      </button>
    </div>
  );
};

export default MetricCard;
